/**
 * IP Allocations Table Component
 *
 * Tabela de alocacoes de IP (estaticas e DHCP leases) de um subnet.
 * Usado na aba IPAM do NetworkingPage.
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import {
  Trash2Icon,
  Loader2Icon,
  ServerIcon,
  RadioIcon,
  RefreshCwIcon,
  AlertTriangleIcon,
} from 'lucide-react'
import { useState } from 'react'

import { endCustomerIpamApi } from '@nebula/shared/services'
import type { AllocationEntry } from '@nebula/shared/services'

type IPAllocationsTableProps = {
  subnetId: string
}

export default function IPAllocationsTable({ subnetId }: IPAllocationsTableProps) {
  const queryClient = useQueryClient()
  const [releasingId, setReleasingId] = useState<string | null>(null)

  const { data, isLoading, error } = useQuery({
    queryKey: ['ec-ipam-allocations', subnetId],
    queryFn: () => endCustomerIpamApi.listAllocations(subnetId),
  })

  const releaseMutation = useMutation({
    mutationFn: (allocationId: string) => endCustomerIpamApi.releaseAllocation(allocationId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['ec-ipam-allocations', subnetId] })
      queryClient.invalidateQueries({ queryKey: ['ec-ipam-availability', subnetId] })
      queryClient.invalidateQueries({ queryKey: ['ec-ipam-subnets'] })
      setReleasingId(null)
    },
    onError: () => {
      setReleasingId(null)
    },
  })

  const handleRelease = (allocation: AllocationEntry) => {
    if (allocation.resource_type === 'dhcp_lease') return
    setReleasingId(allocation.id)
    releaseMutation.mutate(allocation.id)
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-8 text-gray-500">
        <Loader2Icon className="mr-2 size-5 animate-spin" />
        Carregando alocacoes...
      </div>
    )
  }

  if (error) {
    return (
      <div className="flex items-center space-x-2 rounded-lg bg-red-50 p-4 text-red-700">
        <AlertTriangleIcon className="size-5" />
        <span>Erro ao carregar alocações</span>
      </div>
    )
  }

  const allocations = data?.allocations || []

  if (allocations.length === 0) {
    return (
      <div className="py-8 text-center text-gray-500">
        <ServerIcon className="mx-auto mb-2 size-8 text-gray-400" />
        <p>Nenhuma alocação de IP encontrada</p>
        <p className="mt-1 text-xs">
          Alocações aparecerão aqui quando VMs forem criadas ou leases DHCP forem obtidos.
        </p>
      </div>
    )
  }

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <h4 className="text-sm font-medium text-gray-700">
          {allocations.length} alocação{allocations.length === 1 ? '' : 'es'}
        </h4>
        <button
          onClick={() => {
            queryClient.invalidateQueries({ queryKey: ['ec-ipam-allocations', subnetId] })
          }}
          className="inline-flex items-center text-xs text-gray-500 hover:text-gray-700"
        >
          <RefreshCwIcon className="mr-1 size-3" />
          Atualizar
        </button>
      </div>

      <div className="overflow-x-auto">
        <table className="min-w-full text-sm">
          <thead>
            <tr className="border-b border-gray-200">
              <th className="px-3 py-2 text-left font-medium text-gray-600">IP</th>
              <th className="px-3 py-2 text-left font-medium text-gray-600">Tipo</th>
              <th className="hidden px-3 py-2 text-left font-medium text-gray-600 sm:table-cell">
                Hostname
              </th>
              <th className="hidden px-3 py-2 text-left font-medium text-gray-600 md:table-cell">
                MAC
              </th>
              <th className="px-3 py-2 text-left font-medium text-gray-600">Status</th>
              <th className="px-3 py-2 text-right font-medium text-gray-600">Ação</th>
            </tr>
          </thead>
          <tbody>
            {allocations.map((alloc) => (
              <tr key={alloc.id} className="border-b border-gray-100 hover:bg-gray-50">
                <td className="px-3 py-2 font-mono">{alloc.ip_address}</td>
                <td className="px-3 py-2">
                  {alloc.resource_type === 'dhcp_lease' ? (
                    <span className="inline-flex items-center rounded-full bg-green-100 px-2 py-0.5 text-xs text-green-700">
                      <RadioIcon className="mr-1 size-3" />
                      DHCP
                    </span>
                  ) : (
                    <span className="inline-flex items-center rounded-full bg-blue-100 px-2 py-0.5 text-xs text-blue-700">
                      <ServerIcon className="mr-1 size-3" />
                      Estatico
                    </span>
                  )}
                </td>
                <td className="hidden px-3 py-2 text-gray-600 sm:table-cell">
                  {alloc.hostname || '-'}
                </td>
                <td className="hidden px-3 py-2 font-mono text-xs text-gray-500 md:table-cell">
                  {alloc.mac_address || '-'}
                </td>
                <td className="px-3 py-2">
                  <span
                    className={`text-xs font-medium ${
                      alloc.status === 'ALLOCATED'
                        ? 'text-blue-600'
                        : alloc.status === 'RESERVED'
                          ? 'text-amber-600'
                          : 'text-gray-500'
                    }`}
                  >
                    {alloc.status}
                  </span>
                </td>
                <td className="px-3 py-2 text-right">
                  {alloc.resource_type !== 'dhcp_lease' && (
                    <button
                      onClick={() => handleRelease(alloc)}
                      disabled={releasingId === alloc.id}
                      className="inline-flex items-center text-xs text-red-600 hover:text-red-800 disabled:opacity-50"
                      title="Liberar IP"
                    >
                      {releasingId === alloc.id ? (
                        <Loader2Icon className="size-3.5 animate-spin" />
                      ) : (
                        <Trash2Icon className="size-3.5" />
                      )}
                    </button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  )
}
